import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useParams } from "react-router-dom";
import { api } from "../api";

const BRAND = "#154E93";
const GOLD = "#9C7C2C";
const TZ = "America/New_York";
const fmt = (iso, withTime = false) => {
  if (!iso) return "—";
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T12:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat("en-US", { timeZone: TZ, month: "2-digit", day: "2-digit", year: "numeric", ...(withTime ? { hour: "numeric", minute: "2-digit" } : {}) }).format(d);
};
const yes = (v) => (v ? "Yes" : "No");

const STATUS_TONE = {
  Verified: "bg-emerald-50 text-emerald-700 border-emerald-200", "Pending Review": "bg-blue-50 text-blue-700 border-blue-200",
  "Expiring Soon": "bg-amber-50 text-amber-700 border-amber-200", Missing: "bg-red-50 text-red-700 border-red-200",
  Expired: "bg-red-50 text-red-700 border-red-200", Rejected: "bg-rose-50 text-rose-700 border-rose-200",
};
const Pill = ({ children, tone }) => <span className={`inline-block px-2 py-0.5 rounded-full border text-xs font-medium whitespace-nowrap ${tone || "bg-gray-50 text-gray-700 border-gray-200"}`}>{children}</span>;

function useLoad(fn, deps) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { setData(null); setError(""); fn().then(setData).catch((e) => setError(e.message)); }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return [data, error];
}
const Loading = ({ error }) => error ? <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 text-sm">{error}</div> : <div className="text-gray-500 text-sm">Loading…</div>;

function Section({ title, children, right, count }) {
  return (
    <section className="bg-white border rounded-xl shadow-sm print:shadow-none print:break-inside-avoid">
      <div className="px-4 py-3 border-b flex items-center justify-between gap-2">
        <h2 className="font-bold" style={{ color: BRAND }}>{title}{count !== undefined && <span className="ml-2 text-xs font-normal text-gray-500">({count})</span>}</h2>{right}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Table({ cols, rows, empty = "No records on file." }) {
  if (!rows?.length) return <p className="text-sm text-gray-500">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead><tr className="bg-slate-50 text-left text-gray-600">{cols.map(([label]) => <th key={label} className="px-3 py-2 border-b font-semibold whitespace-nowrap">{label}</th>)}</tr></thead>
        <tbody className="divide-y">{rows.map((r, i) => <tr key={i} className="align-top">{cols.map(([label, get]) => <td key={label} className="px-3 py-2">{get(r) ?? "—"}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

const PrintButton = () => <button onClick={() => window.print()} className="px-3 py-1.5 rounded-lg border text-xs font-semibold print:hidden" style={{ borderColor: BRAND, color: BRAND }}>Print / Save PDF</button>;

// ---------------- Overview ----------------
function Overview({ me }) {
  const [d, error] = useLoad(() => api.inspector.overview(), []);
  if (!d) return <Loading error={error} />;
  const p = d.programs;
  const tile = (label, value, helper, warn) => (
    <div className="bg-white border rounded-xl p-4">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-2xl font-bold ${warn ? "text-red-700" : ""}`} style={warn ? {} : { color: BRAND }}>{value}</div>
      {helper && <div className="text-xs text-gray-500 mt-1">{helper}</div>}
    </div>
  );
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {tile("Active staff", d.staff.total, `${d.staff.ready} cleared for scheduling`)}
        {tile("Average file compliance", `${d.staff.average_score}%`, "Across active personnel files")}
        {tile("New hire packets certified", d.staff.packets_certified, "Electronically signed, He-P 822.17")}
        {d.clients ? tile("Active clients", d.clients.active, "Client records available") : tile("Client records", "Not included", "Outside this inspection's scope")}
      </div>
      <Section title="Agency compliance programs">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
          <div><div className="text-gray-500 text-xs">Last QA meeting</div><div className="font-semibold">{fmt(p.qa_last?.meeting_date)}</div><div className="text-xs text-gray-500">{p.qa_count_12m} in last 12 months · next due {fmt(p.qa_last?.next_meeting_due_date)}</div></div>
          <div><div className="text-gray-500 text-xs">Dementia training overdue</div><div className={`font-semibold ${p.dementia_overdue ? "text-red-700" : "text-emerald-700"}`}>{p.dementia_overdue}</div></div>
          <div><div className="text-gray-500 text-xs">TB screenings incomplete</div><div className={`font-semibold ${p.tb_incomplete ? "text-red-700" : "text-emerald-700"}`}>{p.tb_incomplete}</div></div>
          <div><div className="text-gray-500 text-xs">PCA supervision overdue</div><div className={`font-semibold ${p.pca_overdue ? "text-red-700" : "text-emerald-700"}`}>{p.pca_overdue}</div></div>
        </div>
        <Link to="/inspector/programs" className="inline-block mt-4 text-sm font-semibold underline" style={{ color: BRAND }}>View program records</Link>
      </Section>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Link to="/inspector/personnel" className="bg-white border rounded-xl p-5 hover:bg-slate-50"><div className="font-bold" style={{ color: BRAND }}>Personnel files →</div><div className="text-sm text-gray-600">Compliance checklist, evidence documents, signed onboarding packet, training and TB records for each employee.</div></Link>
        <Link to="/inspector/manuals" className="bg-white border rounded-xl p-5 hover:bg-slate-50"><div className="font-bold" style={{ color: BRAND }}>Policies &amp; manuals →</div><div className="text-sm text-gray-600">Policy and procedure manual, employee handbook and other agency plans.</div></Link>
        {me.include_clients && <Link to="/inspector/clients" className="bg-white border rounded-xl p-5 hover:bg-slate-50"><div className="font-bold" style={{ color: BRAND }}>Client records →</div><div className="text-sm text-gray-600">Admission checklist, care plan, quality reviews, incidents, progress notes and documents.</div></Link>}
      </div>
      <p className="text-xs text-gray-500">Data as of {fmt(d.generated_at, true)} ET. This portal is read-only; every page and document opened is recorded in the agency's access log.</p>
    </div>
  );
}

// ---------------- Personnel ----------------
function PersonnelList() {
  const [rows, error] = useLoad(() => api.inspector.personnel(), []);
  const [q, setQ] = useState("");
  const [showTerminated, setShowTerminated] = useState(false);
  const list = useMemo(() => (rows || []).filter((r) => (showTerminated || r.employment_status !== "Terminated") && (!q || `${r.name} ${r.employee_id} ${r.position}`.toLowerCase().includes(q.toLowerCase()))), [rows, q, showTerminated]);
  if (!rows) return <Loading error={error} />;
  return (
    <Section title="Personnel files" count={list.length} right={<PrintButton />}>
      <div className="flex flex-wrap gap-3 items-center mb-3 print:hidden">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, ID or position" className="border rounded-lg px-3 py-1.5 text-sm w-full sm:w-72" />
        <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={showTerminated} onChange={(e) => setShowTerminated(e.target.checked)} /> Include separated staff</label>
      </div>
      <Table rows={list} cols={[
        ["Employee", (r) => <Link className="font-medium underline" style={{ color: BRAND }} to={`/inspector/personnel/${encodeURIComponent(r.employee_id)}`}>{r.name}</Link>],
        ["ID", (r) => r.employee_id],
        ["Position", (r) => r.position || r.staff_category],
        ["Hire date", (r) => fmt(r.hire_date)],
        ["Status", (r) => r.employment_status],
        ["File compliance", (r) => <span className={r.score >= 90 ? "text-emerald-700 font-semibold" : r.score >= 70 ? "text-amber-700 font-semibold" : "text-red-700 font-semibold"}>{r.score}%</span>],
        ["Cleared", (r) => (r.ready ? "Yes" : `No (${r.blockers} blocking)`)],
        ["New hire packet", (r) => (r.packet_status === "certified" ? `Certified ${fmt(r.packet_certified_at)}` : r.packet_status ? r.packet_status.replace(/_/g, " ") : "—")],
      ]} />
    </Section>
  );
}

function PersonnelFile() {
  const { employeeId } = useParams();
  const [d, error] = useLoad(() => api.inspector.personnelFile(employeeId), [employeeId]);
  if (!d) return <Loading error={error} />;
  const e = d.employee;
  const docLink = (doc) => doc?.has_file ? <a href={api.inspector.documentUrl(doc.id)} target="_blank" rel="noreferrer" className="underline" style={{ color: BRAND }}>{doc.original_filename || doc.document_type}</a> : doc ? (doc.original_filename || "Record only") : "—";
  return (
    <div className="space-y-5">
      <Link to="/inspector/personnel" className="text-sm hover:underline print:hidden" style={{ color: BRAND }}>← Personnel files</Link>
      <div className="bg-white border rounded-xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="text-xs font-bold tracking-[0.15em]" style={{ color: GOLD }}>PERSONNEL FILE · He-P 822.17</div>
          <h1 className="text-2xl font-bold" style={{ color: "#1B3A6B" }}>{e.name}</h1>
          <p className="text-sm text-gray-600">{e.employee_id} · {e.position || e.staff_category} · Hired {fmt(e.hire_date)} · {e.employment_status}{e.termination_date ? ` (separated ${fmt(e.termination_date)})` : ""}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right"><div className="text-3xl font-bold" style={{ color: BRAND }}>{d.compliance.score}%</div><div className="text-xs text-gray-500">{d.compliance.ready_for_scheduling ? "Cleared for client contact" : `${d.compliance.blockers} blocking item(s)`}</div></div>
          <PrintButton />
        </div>
      </div>

      <Section title="Compliance checklist" count={d.compliance.requirements.length}>
        <Table rows={d.compliance.requirements} cols={[
          ["Category", (r) => r.category], ["Requirement", (r) => <span className="font-medium">{r.label}</span>],
          ["Status", (r) => <Pill tone={STATUS_TONE[r.status]}>{r.status}</Pill>], ["Evidence", (r) => docLink(r.document)],
          ["Detail", (r) => <span className="text-gray-600">{r.detail}</span>], ["Source", (r) => <span className="text-xs text-gray-500">{r.source}</span>],
        ]} />
      </Section>

      <Section title="New hire packet (electronically signed)">
        {d.onboarding_packet ? (
          <div className="space-y-2 text-sm">
            <p>Status: <b>{d.onboarding_packet.status.replace(/_/g, " ")}</b> · E-sign consent {fmt(d.onboarding_packet.consent_at, true)} · Employee completed {fmt(d.onboarding_packet.completed_at, true)} · Certified {fmt(d.onboarding_packet.certified_at, true)}{d.onboarding_packet.certified_by ? ` by ${d.onboarding_packet.certified_by}` : ""}</p>
            <Table rows={d.onboarding_packet.documents} empty="No signed PDFs generated yet." cols={[
              ["Signed document", (r) => <a className="underline" style={{ color: BRAND }} href={api.inspector.documentUrl(r.document_id)} target="_blank" rel="noreferrer">{r.label}</a>],
              ["Generated", (r) => fmt(r.created_at, true)],
              ["SHA-256 fingerprint", (r) => <code className="text-[11px] break-all">{r.sha256}</code>],
            ]} />
          </div>
        ) : <p className="text-sm text-gray-500">This employee's onboarding records were completed on paper or before the digital packet; see documents below.</p>}
      </Section>

      <Section title="All documents on file" count={d.documents.length}>
        <Table rows={d.documents} cols={[
          ["Type", (r) => r.document_type], ["File", (r) => docLink(r)], ["Issued", (r) => fmt(r.issue_date)],
          ["Expires", (r) => fmt(r.expiration_date)], ["Verified", (r) => `${r.verified_status || "—"}${r.verified_date ? ` ${fmt(r.verified_date)}` : ""}`],
        ]} />
      </Section>

      <div className="grid lg:grid-cols-2 gap-5">
        <Section title="Training records" count={d.training.length}>
          <Table rows={d.training} cols={[["Course", (r) => r.course], ["Date", (r) => fmt(r.training_date)], ["Trainer", (r) => r.trainer], ["Next due", (r) => fmt(r.next_training)]]} />
        </Section>
        <Section title="Dementia training" count={d.dementia_training.length}>
          <Table rows={d.dementia_training} cols={[["Type", (r) => r.training_type], ["Hours", (r) => r.hours], ["Completed", (r) => fmt(r.completion_date)], ["Due", (r) => fmt(r.due_date)], ["Competency", (r) => yes(r.competency_demonstrated)]]} />
        </Section>
        <Section title="TB screening" count={d.tb_testing.length}>
          <Table rows={d.tb_testing} cols={[
            ["Method", (r) => (r.method === "blood_test" ? "Blood test (IGRA)" : "Two-step TST")],
            ["Step 1", (r) => (r.method === "blood_test" ? `${fmt(r.blood_test_date)} ${r.blood_test_result || ""}` : `${fmt(r.step1_date)} ${r.step1_result || ""}`)],
            ["Step 2", (r) => (r.method === "blood_test" ? "—" : `${fmt(r.step2_date)} ${r.step2_result || ""}`)],
            ["Complete", (r) => yes(r.is_complete)],
          ]} />
        </Section>
        <Section title="PCA supervision visits" count={d.pca_supervision.length}>
          <Table rows={d.pca_supervision} cols={[["Date", (r) => fmt(r.supervision_date)], ["Supervisor", (r) => r.supervisor_employee_id], ["Next due", (r) => fmt(r.next_due_date)], ["Notes", (r) => r.notes]]} />
        </Section>
      </div>
    </div>
  );
}

// ---------------- Clients ----------------
function ClientList() {
  const [rows, error] = useLoad(() => api.inspector.clients(), []);
  const [q, setQ] = useState("");
  if (!rows) return <Loading error={error} />;
  const list = rows.filter((r) => !q || `${r.name} ${r.client_id}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <Section title="Client records" count={list.length} right={<PrintButton />}>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or ID" className="border rounded-lg px-3 py-1.5 text-sm w-full sm:w-72 mb-3 print:hidden" />
      <Table rows={list} cols={[
        ["Client", (r) => <Link className="font-medium underline" style={{ color: BRAND }} to={`/inspector/clients/${encodeURIComponent(r.client_id)}`}>{r.name}</Link>],
        ["ID", (r) => r.client_id], ["Status", (r) => r.status || "Active"], ["Care type", (r) => r.care_type || r.care_level],
        ["Payer", (r) => r.payer_source], ["Admitted", (r) => fmt(r.admission_date)], ["Discharged", (r) => fmt(r.discharge_date)],
      ]} />
    </Section>
  );
}

function ClientRecord() {
  const { clientId } = useParams();
  const [d, error] = useLoad(() => api.inspector.client(clientId), [clientId]);
  if (!d) return <Loading error={error} />;
  const c = d.client;
  const info = [["Client ID", c.client_id], ["Date of birth", fmt(c.date_of_birth)], ["Status", c.status], ["Care type / level", [c.care_type, c.care_level].filter(Boolean).join(" · ")],
    ["Admission", fmt(c.admission_date)], ["Discharge", fmt(c.discharge_date)], ["Payer", c.payer_source], ["Authorization", c.authorization_start || c.authorization_end || c.authorized_hours_per_week ? `${fmt(c.authorization_start)} – ${fmt(c.authorization_end)}${c.authorized_hours_per_week ? ` · ${c.authorized_hours_per_week} hrs/wk` : ""}` : null],
    ["Primary diagnosis", c.primary_diagnosis], ["Allergies", c.allergies], ["Mobility", c.mobility_status], ["Physician", c.physician_name], ["Case manager", c.case_manager],
    ["Emergency contact", c.emergency_contact], ["Authorized agent", c.authorized_agent], ["Location", [c.city, c.state].filter(Boolean).join(", ")]];
  return (
    <div className="space-y-5">
      <Link to="/inspector/clients" className="text-sm hover:underline print:hidden" style={{ color: BRAND }}>← Client records</Link>
      <div className="bg-white border rounded-xl p-5">
        <div className="flex flex-col sm:flex-row justify-between gap-3">
          <div><div className="text-xs font-bold tracking-[0.15em]" style={{ color: GOLD }}>CLIENT RECORD</div><h1 className="text-2xl font-bold" style={{ color: "#1B3A6B" }}>{c.name}</h1></div>
          <PrintButton />
        </div>
        <dl className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 mt-4 text-sm">{info.map(([k, v]) => <div key={k}><dt className="text-xs text-gray-500">{k}</dt><dd className="font-medium">{v || "—"}</dd></div>)}</dl>
        {c.special_instructions && <p className="text-sm mt-3"><span className="text-gray-500">Special instructions:</span> {c.special_instructions}</p>}
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <Section title="Admission checklist" count={d.admission_items.length}><Table rows={d.admission_items} cols={[["Item", (r) => r.item_name], ["Status", (r) => r.status], ["Completed", (r) => fmt(r.completed_date)]]} /></Section>
        <Section title="Compliance items" count={d.compliance_items.length}><Table rows={d.compliance_items} cols={[["Item", (r) => r.item_name], ["Status", (r) => r.status], ["Completed", (r) => fmt(r.completed_date)], ["Renewal", (r) => fmt(r.renewal_due)]]} /></Section>
      </div>
      <Section title="Care plan" count={d.care_plan.length}>
        <Table rows={d.care_plan} cols={[["Domain", (r) => r.domain], ["Need", (r) => r.need], ["Goal", (r) => r.goal], ["Intervention", (r) => r.intervention], ["Frequency", (r) => r.frequency], ["Assistance", (r) => r.assistance_level], ["Status", (r) => r.status]]} />
      </Section>
      <Section title="Quality reviews / supervisory visits" count={d.quality_reviews.length}>
        <Table rows={d.quality_reviews} cols={[["Date", (r) => fmt(r.review_date)], ["Type", (r) => r.review_type], ["By", (r) => r.reviewed_by], ["Rating", (r) => r.overall_rating], ["Findings", (r) => r.findings], ["Corrective action", (r) => r.corrective_action], ["Status", (r) => r.status]]} />
      </Section>
      <Section title="Incidents" count={d.incidents.length}>
        <Table rows={d.incidents} empty="No incidents recorded." cols={[["Date", (r) => fmt(r.incident_date)], ["Type", (r) => r.incident_type], ["Severity", (r) => r.severity], ["Description", (r) => r.description], ["Action / notifications", (r) => [r.immediate_action, r.notifications].filter(Boolean).join(" · ")], ["Status", (r) => `${r.status || ""}${r.resolved_at ? ` ${fmt(r.resolved_at)}` : ""}`]]} />
      </Section>
      <Section title="Progress notes (most recent 60)" count={d.progress_notes.length}>
        <Table rows={d.progress_notes} cols={[["Date", (r) => fmt(r.note_date)], ["Caregiver", (r) => r.employee_name], ["Services", (r) => r.services_provided], ["Client response / changes", (r) => [r.client_response, r.changes_observed].filter(Boolean).join(" · ")], ["Reviewed", (r) => r.reviewed_by || r.status]]} />
      </Section>
      <Section title="Signed agreements (electronic, approved by Jybek)" count={(d.signed_agreements || []).length}>
        <Table rows={d.signed_agreements || []} empty="No electronically signed agreements yet — paper originals, if any, are listed under Documents." cols={[
          ["Document", (r) => r.document_id ? <a className="underline" style={{ color: BRAND }} href={api.inspector.clientDocumentUrl(c.client_id, r.document_id)} target="_blank" rel="noreferrer">{r.title}</a> : r.title],
          ["Signed by", (r) => `${r.adopted_name || r.signer_name} (${r.signer_relationship})`],
          ["Signed", (r) => fmt(r.signer_signed_at)], ["Approved", (r) => `${fmt(r.approved_at)}${r.approved_by ? ` · ${r.approved_by}` : ""}`],
          ["SHA-256", (r) => <code className="text-[10px]">{(r.sha256 || "").slice(0, 16)}…</code>],
        ]} />
      </Section>
      <Section title="Documents" count={d.documents.length}>
        <Table rows={d.documents} cols={[
          ["Folder", (r) => r.folder], ["Type", (r) => r.document_type],
          ["File", (r) => <a className="underline" style={{ color: BRAND }} href={api.inspector.clientDocumentUrl(c.client_id, r.id)} target="_blank" rel="noreferrer">{r.file_name}</a>],
          ["Issued", (r) => fmt(r.issue_date)], ["Expires", (r) => fmt(r.expiration_date)], ["Status", (r) => r.status],
        ]} />
      </Section>
    </div>
  );
}

// ---------------- Programs ----------------
function Programs() {
  const [d, error] = useLoad(() => api.inspector.programs(), []);
  if (!d) return <Loading error={error} />;
  const attendees = (a) => { try { const x = JSON.parse(a); return Array.isArray(x) ? x.join(", ") : a; } catch (_e) { return a; } };
  return (
    <div className="space-y-5">
      <div className="flex justify-end print:hidden"><PrintButton /></div>
      <Section title="Quality assurance meetings" count={d.qa_meetings.length}>
        <Table rows={d.qa_meetings} cols={[["Date", (r) => fmt(r.meeting_date)], ["Attendees", (r) => attendees(r.attendees)], ["Topics", (r) => r.topics_covered], ["Next due", (r) => fmt(r.next_meeting_due_date)]]} />
      </Section>
      <Section title="Dementia training (all staff)" count={d.dementia_training.length}>
        <Table rows={d.dementia_training} cols={[["Employee", (r) => r.employee_name || r.employee_id], ["Type", (r) => r.training_type], ["Hours", (r) => r.hours], ["Completed", (r) => fmt(r.completion_date)], ["Due", (r) => fmt(r.due_date)], ["Competency", (r) => yes(r.competency_demonstrated)], ["Certificate", (r) => yes(r.certificate_issued)]]} />
      </Section>
      <Section title="TB screening (all staff)" count={d.tb_testing.length}>
        <Table rows={d.tb_testing} cols={[["Employee", (r) => r.employee_name || r.employee_id], ["Method", (r) => (r.method === "blood_test" ? "Blood test" : "Two-step TST")], ["Step 1 / test", (r) => (r.method === "blood_test" ? `${fmt(r.blood_test_date)} ${r.blood_test_result || ""}` : `${fmt(r.step1_date)} ${r.step1_result || ""}`)], ["Step 2", (r) => (r.method === "blood_test" ? "—" : `${fmt(r.step2_date)} ${r.step2_result || ""}`)], ["Complete", (r) => yes(r.is_complete)]]} />
      </Section>
      <Section title="PCA supervision (all staff)" count={d.pca_supervision.length}>
        <Table rows={d.pca_supervision} cols={[["Employee", (r) => r.employee_name || r.employee_id], ["Date", (r) => fmt(r.supervision_date)], ["Next due", (r) => fmt(r.next_due_date)], ["Notes", (r) => r.notes]]} />
      </Section>
    </div>
  );
}

// ---------------- Policies & manuals ----------------
function Manuals() {
  const [rows, error] = useLoad(() => api.inspector.manuals(), []);
  if (!rows) return <Loading error={error} />;
  const size = (b) => (b ? `${(b / 1048576).toFixed(1)} MB` : "");
  return (
    <Section title="Policies, procedures & manuals" count={rows.length}>
      {rows.length === 0 ? <p className="text-sm text-gray-500">No manuals have been shared yet. Ask the agency for its current policy and procedure manual.</p> : (
        <div className="grid md:grid-cols-2 gap-3">
          {rows.map((m) => (
            <a key={m.id} href={api.inspector.manualUrl(m.id)} target="_blank" rel="noreferrer" className="block border rounded-xl p-4 hover:bg-slate-50">
              <div className="text-[11px] font-bold tracking-widest uppercase" style={{ color: GOLD }}>{m.category}</div>
              <div className="font-bold text-lg" style={{ color: "#1B3A6B" }}>{m.title}</div>
              <div className="text-sm text-gray-600">{[m.version, m.effective_date && `Effective ${fmt(m.effective_date)}`].filter(Boolean).join(" · ") || "—"}</div>
              <div className="text-xs text-gray-500 mt-2">{m.original_filename} · {size(m.file_size)} · <span className="underline" style={{ color: BRAND }}>Open</span></div>
            </a>
          ))}
        </div>
      )}
    </Section>
  );
}

// ---------------- Shell ----------------
export default function InspectorPortal({ user, onLogout }) {
  const [me, error] = useLoad(() => api.inspector.me(), []);
  const logout = async () => { try { await api.logout(); } finally { onLogout(); } };
  if (!me) return <div className="p-6"><Loading error={error} /></div>;
  const tab = ({ isActive }) => `px-3 py-2 text-sm font-semibold rounded-lg whitespace-nowrap ${isActive ? "text-white" : "text-gray-600 hover:bg-slate-100"}`;
  const tabStyle = ({ isActive }) => (isActive ? { backgroundColor: BRAND } : {});
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b print:border-0">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Jybek HomeCare Services" className="h-10" />
            <div>
              <div className="font-bold leading-tight" style={{ color: "#1B3A6B" }}>{me.agency_name}</div>
              <div className="text-xs text-gray-500">{me.agency_address} · Inspection portal (read-only)</div>
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm print:hidden">
            <div className="text-right">
              <div className="font-medium">{me.name}</div>
              <div className="text-xs text-gray-500">{me.agency}{me.expires_at ? ` · access until ${fmt(me.expires_at, true)} ET` : ""}</div>
            </div>
            {me.preview ? <Link to="/admin/inspector-access" className="px-3 py-1.5 rounded-lg border text-xs font-semibold">Exit preview</Link>
              : <button onClick={logout} className="px-3 py-1.5 rounded-lg border text-xs font-semibold">Sign out</button>}
          </div>
        </div>
        {me.preview && <div className="bg-amber-50 border-t border-amber-200 text-amber-900 text-xs text-center py-1.5 print:hidden">Administrator preview — this is exactly what an inspector sees. Your views here are not logged as inspector activity.</div>}
        <nav className="max-w-7xl mx-auto px-4 pb-3 flex gap-1 overflow-x-auto print:hidden">
          <NavLink end to="/inspector" className={tab} style={tabStyle}>Overview</NavLink>
          <NavLink to="/inspector/personnel" className={tab} style={tabStyle}>Personnel files</NavLink>
          {me.include_clients && <NavLink to="/inspector/clients" className={tab} style={tabStyle}>Client records</NavLink>}
          <NavLink to="/inspector/programs" className={tab} style={tabStyle}>Agency programs</NavLink>
          <NavLink to="/inspector/manuals" className={tab} style={tabStyle}>Policies &amp; manuals</NavLink>
        </nav>
      </header>
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Routes>
          <Route index element={<Overview me={me} />} />
          <Route path="personnel" element={<PersonnelList />} />
          <Route path="personnel/:employeeId" element={<PersonnelFile />} />
          <Route path="clients" element={me.include_clients ? <ClientList /> : <Navigate to="/inspector" replace />} />
          <Route path="clients/:clientId" element={me.include_clients ? <ClientRecord /> : <Navigate to="/inspector" replace />} />
          <Route path="programs" element={<Programs />} />
          <Route path="manuals" element={<Manuals />} />
          <Route path="*" element={<Navigate to="/inspector" replace />} />
        </Routes>
      </main>
    </div>
  );
}
